import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAppStore } from '@/services/store'
import { useInstallPrompt } from '@/hooks/useInstallPrompt'
import { useAuth } from '@/contexts/AuthContext'
import { useUserProfile } from '@/contexts/UserProfileContext'
import { analytics } from '@/services/analytics'
import {
  HomeIcon,
  BookOpenIcon,
  UsersIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const {
    // theme,
    settings,
  } = useAppStore()
  const { isInstallable, promptInstall } = useInstallPrompt()
  const { user, signOut } = useAuth()
  const { childrenData, activeChild, setActiveChild } = useUserProfile()
  const [showChildSwitcher, setShowChildSwitcher] = useState(false)

  const navigation = [
    {
      name: 'Home',
      href: '/',
      icon: HomeIcon,
    },
    { name: 'Community', href: '/library/community', icon: UsersIcon },
    { name: 'My Creations', href: '/library/my', icon: BookOpenIcon },
  ]

  const isActive = (path: string) => location.pathname === path

  const playSound = () => {
    if (settings.soundEffects && 'Audio' in window) {
      const audio = new Audio(
        'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBiuBzvLZiTYIG2m98OScTgwOUarm7blmFgU7k9n1unEiBC13yO/eizEIHWq+8+OWT'
      )
      audio.volume = 0.3
      audio.play().catch(() => {})
    }
  }

  const handleChildSwitch = async (child: (typeof childrenData)[0]) => {
    playSound()
    setActiveChild(child)
    setShowChildSwitcher(false)

    // Track profile switch
    await analytics.trackProfileUsage(child.id, 'switched')
  }

  const getAvatarColor = (index: number) => {
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-yellow-500']
    return colors[index % colors.length]
  }

  const getInitials = (name: string) => {
    if (!name) return 'AR'
    const words = name.trim().split(' ')
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase()
    }
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  const isFocusedPage = location.pathname.startsWith('/auth') || location.pathname.startsWith('/onboarding')

  return (
    <div className="min-h-screen bg-brand-cloud dark:bg-[#2E3A59]  ">
      {/* Header */}
      {!isFocusedPage && (
        <header className="sticky top-0 z-50 bg-brand-cloud dark:bg-[#2E3A59] shadow-sm safe-top w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
            <div className="h-16 flex items-center justify-between ">
              {/* App Logo */}
              <img
                src="/brand/logo.svg"
                alt="BrAIght Kids"
                className="h-16 w-auto lg:h-20 lg:w-auto object-contain "
              />

              {/* User Info and Actions */}
              <div className="flex items-center gap-4">
                {/* Child Profile Switcher */}
                {activeChild && childrenData.length > 0 && (
                  <div className="relative ">
                    <button
                      onClick={() => {
                        playSound()
                        setShowChildSwitcher(!showChildSwitcher)
                      }}
                      className="flex items-center gap-3 bg-neutral-200 dark:bg-[#314162] rounded-full px-4 py-3 hover:bg-neutral-300 dark:hover:bg-[#3a4b73] transition-colors h-16"
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${getAvatarColor(
                          childrenData.findIndex((c) => c.id === activeChild?.id)
                        )}`}
                      >
                        {getInitials(activeChild.name)}
                      </div>
                      <span className="text-base md:text-lg font-semibold text-gray-700 dark:text-gray-300">
                        {activeChild.name}
                      </span>
                      <ChevronDownIcon
                        className={`w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform ${
                          showChildSwitcher ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {showChildSwitcher && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute top-full mt-2 right-0 bg-brand-cloud dark:bg-[#314162] rounded-xl shadow-xl p-2 z-50 min-w-48 max-h-80 overflow-y-auto flex flex-col"
                      >
                        <div className="flex-1 overflow-y-auto">
                          {childrenData.map((child, index) => (
                            <button
                              key={child.id}
                              onClick={() => handleChildSwitch(child)}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-[#3a4b73] transition-colors ${
                                child.id === activeChild?.id ? 'bg-neutral-200 dark:bg-[#3a4b73]' : ''
                              }`}
                            >
                              <div
                                className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-white text-sm font-bold ${getAvatarColor(
                                  index
                                )}`}
                              >
                                {getInitials(child.name)}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white text-base">
                                {child.name}
                              </span>
                            </button>
                          ))}
                        </div>

                        {/* Footer Links */}
                        <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 flex-shrink-0">
                          <Link
                            to="/profiles"
                            onClick={() => {
                              playSound()
                              setShowChildSwitcher(false)
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <span className="text-sm">👤</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                              Manage Profiles
                            </span>
                          </Link>
                          <Link
                            to="/settings"
                            onClick={() => {
                              playSound()
                              setShowChildSwitcher(false)
                            }}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-200 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <span className="text-sm">⚙️</span>
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white text-sm">
                              Settings
                            </span>
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Sign Out Button */}
                {user && (
                  <button
                    onClick={async () => {
                      await signOut()
                      navigate('/auth/login')
                    }}
                    className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors flex items-center gap-1"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </button>
                )}

                {/* Install App Button */}
                {isInstallable && (
                  <button
                    onClick={promptInstall}
                    className="text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    Install
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 w-full max-w-7xl mx-auto ${
          isFocusedPage ? 'py-0' : 'py-6 lg:py-10 pb-24'
        }`}
      >
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      </main>

      {/* Bottom Navigation */}
      {!isFocusedPage && (
        <nav className="fixed bottom-0 left-0 right-0 bg-brand-cloud dark:bg-[#2E3A59] border-t border-gray-200 dark:border-[#3a4b73] shadow-2xl safe-bottom z-50">
          <div className="max-w-7xl mx-auto grid grid-cols-3 h-20">
            {
              // user &&
              //   navigation.map((item) => {
              //     const Icon = item.icon
              //     const active = isActive(item.href)

              //     return (
              //       <Link
              //         key={item.name}
              //         to={item.href}
              //         className={`
              //         flex flex-col items-center justify-center gap-1 no-select py-2
              //         ${
              //           active
              //             ? 'text-[#F18701] dark:text-[#F4C921]'
              //             : 'text-gray-400 dark:text-gray-300'
              //         }
              //         hover:text-[#F18701] dark:hover:text-[#F4C921] transition-colors
              //       `}
              //       >
              //         <Icon className="w-7 h-7" />
              //         <span className="text-xs font-medium font-comfortaa">{item.name}</span>
              //         {active && (
              //           <motion.div
              //             layoutId="activeTab"
              //             className="absolute bottom-0 w-full h-1 bg-[#F18701] dark:bg-[#F4C921] rounded-t-full"
              //           />
              //         )}
              //       </Link>
              //     )
              //   })

              (user ? navigation : navigation.filter((item) => item.name === 'Home')).map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
            relative flex flex-col items-center justify-center gap-1 no-select py-2
            ${active ? 'text-[#F18701] dark:text-[#F4C921]' : 'text-gray-400 dark:text-gray-300'}
            hover:text-[#F18701] dark:hover:text-[#F4C921] 
            transition-colors
          `}
                  >
                    <Icon className="w-7 h-7" />
                    <span className="text-xs font-medium font-comfortaa">{item.name}</span>

                    {active && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute bottom-0 w-full h-1 bg-[#F18701] dark:bg-[#F4C921] rounded-t-full"
                      />
                    )}
                  </Link>
                )
              })
            }
          </div>
        </nav>
      )}
    </div>
  )
}
